import type { ErrorCode, ErrorEnvelope, Tier } from './types';

/**
 * Why a call failed, in four kinds:
 *
 * - `api`       rypt answered with its error envelope. `code` is one of the 46.
 * - `transport` no answer, or an answer that is not rypt's envelope. A Cloud Run HTML
 *               502 or a Cloudflare block page must never masquerade as an API error.
 * - `config`    this build is wrong: Auth0 issued an opaque token, or one for another
 *               audience. Signing in again would produce another one, so re-auth is the
 *               wrong response and a config page is the right one.
 * - `auth`      the session could not be renewed silently and interaction is required.
 */
export type RyptErrorKind = 'api' | 'transport' | 'config' | 'auth';
export type ConfigCode = 'opaque_token' | 'wrong_audience';

export interface CallCtx {
  /** The operationId, for copy that names what failed. */
  op: string;
  keyId?: string;
  keyName?: string;
  /** The tier a create asked for, so a billing refusal can say which kind of key it refused. */
  tier?: Tier;
  /** The version a retire or a restore of one version named, so its refusal can name it. */
  version?: string;
  period?: string;
  requestId?: string;
}

/** crypto.randomUUID matches the API's [A-Za-z0-9_.:-]{1,128} rule, so it is reused verbatim. */
export function newRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `rypt-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

function isEnvelope(body: unknown): body is ErrorEnvelope {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as { error?: unknown }).error === 'string' &&
    typeof (body as { message?: unknown }).message === 'string' &&
    typeof (body as { request_id?: unknown }).request_id === 'string'
  );
}

export class RyptError extends Error {
  readonly kind: RyptErrorKind;
  readonly status: number;
  readonly code: ErrorCode | ConfigCode | 'transport' | 'interaction_required';
  readonly requestId: string;
  readonly ctx: CallCtx;
  /** Present only on ops_cap_reached. */
  readonly cap: number | undefined;
  readonly resetsAt: string | undefined;
  override readonly cause: unknown;

  private constructor(init: {
    kind: RyptErrorKind;
    status: number;
    code: RyptError['code'];
    message: string;
    requestId: string;
    ctx: CallCtx;
    cap?: number | undefined;
    resetsAt?: string | undefined;
    cause?: unknown;
  }) {
    super(init.message);
    this.name = 'RyptError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.ctx = init.ctx;
    this.cap = init.cap;
    this.resetsAt = init.resetsAt;
    this.cause = init.cause;
  }

  /**
   * The ONLY re-auth trigger in the product.
   *
   * Deliberately false for 403 api_token_required / session_required / no_tenant and for
   * 503 unavailable. A 403 means the credential is the wrong *kind*, not expired; a 503
   * can mean rypt cannot reach Auth0's JWKS, which says nothing about the session.
   * Treating either as "sign in again" produces a redirect loop that never resolves.
   */
  get isReauth(): boolean {
    return this.kind === 'auth' || (this.status === 401 && this.code === 'unauthorized');
  }

  static fromResponse(response: Response, body: unknown, ctx: CallCtx): RyptError {
    const headerId = response.headers.get('x-request-id');
    if (isEnvelope(body)) {
      const extra = body as ErrorEnvelope & { cap?: number; resets_at?: string };
      return new RyptError({
        kind: 'api',
        status: response.status,
        code: body.error,
        message: body.message,
        requestId: body.request_id || headerId || ctx.requestId || newRequestId(),
        ctx,
        cap: extra.cap,
        resetsAt: extra.resets_at,
      });
    }
    // Answered, but not by rypt's error layer.
    return new RyptError({
      kind: 'transport',
      status: response.status,
      code: 'transport',
      message: `${response.status} ${response.statusText || 'error'} with no rypt error envelope`,
      requestId: headerId || ctx.requestId || newRequestId(),
      ctx,
      cause: body,
    });
  }

  static transport(cause: unknown, ctx: CallCtx): RyptError {
    return new RyptError({
      kind: 'transport',
      status: 0,
      code: 'transport',
      message: cause instanceof Error ? cause.message : 'the request never got an answer',
      requestId: ctx.requestId || newRequestId(),
      ctx,
      cause,
    });
  }

  static config(code: ConfigCode, message: string, ctx: CallCtx): RyptError {
    return new RyptError({
      kind: 'config',
      status: 0,
      code,
      message,
      requestId: ctx.requestId || newRequestId(),
      ctx,
    });
  }

  static interactionRequired(cause: unknown, ctx: CallCtx): RyptError {
    return new RyptError({
      kind: 'auth',
      status: 0,
      code: 'interaction_required',
      message: 'the session could not be renewed without interaction',
      requestId: ctx.requestId || newRequestId(),
      ctx,
      cause,
    });
  }
}

export const isRyptError = (e: unknown): e is RyptError => e instanceof RyptError;
